// Sprint T-22 · NotificationCenter · modal expandido (full-featured)
//
// Composición:
//   · Tabs: “Bandeja” · “Preferencias”
//   · Bandeja: filtros (read state + kind) + búsqueda + lista infinite scroll
//              + acciones por item (leído/no leído/dismiss)
//   · Preferencias: matriz kind × canal (NotificationPreferencesPanel)
//
// Modal renderizado con `Dialog` de shadcn (bloquea scroll + focus trap).
// En mobile ocupa toda la pantalla · en desktop centrado con max-w-2xl.

import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { BellRinging, Check, Gear, ListBullets, X } from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "../ui/dialog";
import { NotificationFilters } from "./NotificationFilters";
import { NotificationSearch } from "./NotificationSearch";
import { NotificationList } from "./NotificationList";
import { NotificationPreferencesPanel } from "./NotificationPreferencesPanel";
import { api } from "../../lib/api";

const PAGE_SIZE = 20;

export const NotificationCenter = ({ open, onOpenChange, apiClient = api }) => {
  const navigate = useNavigate();
  const [tab, setTab] = useState("inbox");

  // Estado bandeja
  const [items, setItems] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [readFilter, setReadFilter] = useState("all"); // all | unread | read
  const [kindFilter, setKindFilter] = useState("");
  const [search, setSearch] = useState("");
  const [markingAll, setMarkingAll] = useState(false);

  // Fetch inbox (con filtros)
  const fetchInbox = useCallback(
    async ({ skip = 0, append = false } = {}) => {
      if (append) setLoadingMore(true);
      else setLoading(true);
      setError(null);
      try {
        const params = new URLSearchParams({
          limit: PAGE_SIZE.toString(),
          skip: skip.toString(),
        });
        if (readFilter === "unread") params.set("unread_only", "true");
        if (kindFilter) params.set("kind", kindFilter);
        if (search) params.set("q", search);
        const r = await apiClient.get(`/notifications/inbox?${params.toString()}`);
        const data = r?.data || {};
        let nextItems = data.items || [];

        // El backend no soporta filtro "read" directamente (unread_only es booleano);
        // lo filtramos en el cliente cuando el usuario selecciona "Leídas".
        if (readFilter === "read") {
          nextItems = nextItems.filter((n) => n.read);
        }

        setItems((prev) => (append ? [...prev, ...nextItems] : nextItems));
        setTotalCount(data.total_count || 0);
        setHasMore(!!data.has_more);
      } catch (e) {
        const status = e?.response?.status;
        if (status === 401) {
          setError(null);
          setItems([]);
        } else {
          setError("No se pudo cargar la bandeja");
        }
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [apiClient, readFilter, kindFilter, search],
  );

  // Al abrir el modal → fetch inicial
  useEffect(() => {
    if (open && tab === "inbox") {
      fetchInbox({ skip: 0, append: false });
    }
  }, [open, tab, fetchInbox]);

  const handleLoadMore = () => {
    fetchInbox({ skip: items.length, append: true });
  };

  const handleOpen = async (n) => {
    // marcar leído si estaba no leído
    if (!n.read) {
      setItems((prev) =>
        prev.map((it) =>
          it.id === n.id
            ? { ...it, read: true, read_at: new Date().toISOString() }
            : it,
        ),
      );
      try {
        await apiClient.post(`/notifications/${n.id}/read`);
      } catch {
        /* silencioso */
      }
    }
    if (n.deep_link) {
      onOpenChange(false);
      if (/^https?:\/\//i.test(n.deep_link)) {
        window.location.href = n.deep_link;
      } else {
        navigate(n.deep_link);
      }
    }
  };

  const handleMarkRead = async (id) => {
    setItems((prev) =>
      prev.map((it) =>
        it.id === id
          ? { ...it, read: true, read_at: new Date().toISOString() }
          : it,
      ),
    );
    try {
      await apiClient.post(`/notifications/${id}/read`);
      toast.success("Notificación marcada como leída");
    } catch {
      toast.error("No se pudo marcar como leída");
    }
  };

  const handleMarkUnread = async (id) => {
    setItems((prev) =>
      prev.map((it) =>
        it.id === id ? { ...it, read: false, read_at: null } : it,
      ),
    );
    try {
      await apiClient.post(`/notifications/${id}/unread`);
      toast.success("Notificación marcada como no leída");
    } catch {
      toast.error("No se pudo marcar como no leída");
    }
  };

  const handleDismiss = async (id) => {
    // Optimistic: quitamos el item de la lista visible
    const prevItems = items;
    setItems((prev) => prev.filter((it) => it.id !== id));
    setTotalCount((c) => Math.max(0, c - 1));
    try {
      await apiClient.delete(`/notifications/${id}`);
      toast.success("Notificación descartada");
    } catch {
      setItems(prevItems);
      toast.error("No se pudo descartar la notificación");
    }
  };

  const handleMarkAll = async () => {
    setMarkingAll(true);
    try {
      await apiClient.post("/notifications/read-all");
      setItems((prev) =>
        prev.map((it) => ({
          ...it,
          read: true,
          read_at: it.read_at || new Date().toISOString(),
        })),
      );
      toast.success("Todas marcadas como leídas");
    } catch {
      toast.error("No se pudo marcar todas");
    } finally {
      setMarkingAll(false);
    }
  };

  const unreadOnly = readFilter === "unread";
  const readOnly = readFilter === "read";
  const hasSearch = !!search;
  const hasKindFilter = !!kindFilter;

  // Contador de no leídas visible en cabecera
  const displayedUnread = items.filter((n) => !n.read).length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="p-0 gap-0 max-w-2xl w-[95vw] sm:w-[92vw] md:w-full h-[92vh] sm:h-[85vh] md:h-[80vh] max-h-[820px] flex flex-col bg-[var(--ag-midnight)] border-[var(--ag-border)] text-white overflow-hidden"
        data-testid="notification-center"
      >
        {/* Header */}
        <div className="px-5 py-4 border-b border-[var(--ag-border)] flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-2 min-w-0">
            <BellRinging
              size={20}
              weight="fill"
              className="text-[var(--ag-amber,#f59e0b)] shrink-0"
            />
            <div className="min-w-0">
              <DialogTitle className="text-base font-semibold text-white leading-tight">
                Centro de notificaciones
              </DialogTitle>
              <DialogDescription className="text-xs text-[var(--ag-text-dim)] mt-0.5">
                Bandeja, filtros y preferencias por categoría
              </DialogDescription>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div
          className="border-b border-[var(--ag-border)] px-3 flex items-center gap-1 flex-shrink-0"
          role="tablist"
          aria-label="Secciones del centro de notificaciones"
        >
          <button
            type="button"
            role="tab"
            aria-selected={tab === "inbox"}
            onClick={() => setTab("inbox")}
            className={[
              "px-3 py-2.5 text-xs font-semibold flex items-center gap-2 border-b-2 -mb-px transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ag-amber,#f59e0b)]/50 rounded-t-md",
              tab === "inbox"
                ? "text-white border-[var(--ag-amber,#f59e0b)]"
                : "text-[var(--ag-text-dim)] border-transparent hover:text-white",
            ].join(" ")}
            data-testid="notif-center-tab-inbox"
          >
            <ListBullets size={13} weight="bold" />
            Bandeja
            {totalCount > 0 && (
              <span className="text-[10px] font-normal opacity-70">
                · {totalCount}
              </span>
            )}
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={tab === "preferences"}
            onClick={() => setTab("preferences")}
            className={[
              "px-3 py-2.5 text-xs font-semibold flex items-center gap-2 border-b-2 -mb-px transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ag-amber,#f59e0b)]/50 rounded-t-md",
              tab === "preferences"
                ? "text-white border-[var(--ag-amber,#f59e0b)]"
                : "text-[var(--ag-text-dim)] border-transparent hover:text-white",
            ].join(" ")}
            data-testid="notif-center-tab-preferences"
          >
            <Gear size={13} weight="bold" />
            Preferencias
          </button>
        </div>

        {/* Contenido */}
        {tab === "inbox" && (
          <>
            {/* Toolbar */}
            <div
              className="px-4 py-3 border-b border-[var(--ag-border)] flex flex-col gap-3 flex-shrink-0"
              data-testid="notif-center-toolbar"
            >
              <NotificationSearch
                value={search}
                onDebouncedChange={setSearch}
                placeholder="Buscar en título o mensaje…"
              />
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <NotificationFilters
                  readFilter={readFilter}
                  onReadFilterChange={setReadFilter}
                  kindFilter={kindFilter}
                  onKindFilterChange={setKindFilter}
                />
                <button
                  type="button"
                  onClick={handleMarkAll}
                  disabled={markingAll || displayedUnread === 0}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-[var(--ag-text-dim)] hover:text-white border border-[var(--ag-border)] rounded-md hover:bg-[var(--ag-midnight)]/70 disabled:opacity-40 disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ag-amber,#f59e0b)]/50"
                  data-testid="notif-center-mark-all"
                >
                  <Check size={12} weight="bold" />
                  {markingAll ? "Marcando…" : "Marcar todas como leídas"}
                </button>
              </div>
            </div>

            {/* Lista */}
            <div
              className="flex-1 min-h-0 overflow-y-auto"
              data-testid="notif-center-scroll-area"
            >
              <NotificationList
                items={items}
                loading={loading}
                loadingMore={loadingMore}
                hasMore={hasMore && readFilter !== "read"}
                error={error}
                onLoadMore={handleLoadMore}
                onOpen={handleOpen}
                onMarkRead={handleMarkRead}
                onMarkUnread={handleMarkUnread}
                onDismiss={handleDismiss}
                unreadOnly={unreadOnly}
                hasSearch={hasSearch}
                hasKindFilter={hasKindFilter}
              />
            </div>
          </>
        )}

        {tab === "preferences" && (
          <div
            className="flex-1 min-h-0 overflow-y-auto p-5"
            data-testid="notif-center-preferences-area"
          >
            <NotificationPreferencesPanel apiClient={apiClient} />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default NotificationCenter;
